import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  EyeOffIcon,
  FlagIcon,
  InfoIcon,
  LinkIcon,
  Loader2Icon,
  MailIcon,
  PencilIcon,
  PlayCircleIcon,
  RefreshCwIcon,
  RulerIcon,
  SaveIcon,
  SearchIcon,
  SendIcon,
  TargetIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { supabase } from "../../../../lib/supabaseClient";
import { fetchGolfCourseDetails } from "../../../../services/golfCourses";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const splitEmails = (value = "") =>
  [
    ...new Set(
      value
        .split(/[\n,;]+/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];

const createInviteToken = () => {
  const first = window.crypto
    .randomUUID()
    .replaceAll("-", "");
  const second = window.crypto
    .randomUUID()
    .replaceAll("-", "");

  return `${first}${second}`;
};

const getMode = (round) =>
  round?.modes?.[0]?.toLowerCase() || "";

const isScrambleRound = (round) =>
  getMode(round).includes("scramble");

const isHandicapAdjustedRound = (round) => {
  const mode = getMode(round).replaceAll("-", " ");
  return mode.includes("handicap") && mode.includes("slagspel");
};

const getLiveMiniGameType = (miniGame) => {
  if (["closest_to_pin", "longest_drive"].includes(miniGame?.game_type)) {
    return miniGame.game_type;
  }

  const name = String(miniGame?.name || "").toLowerCase();
  if (name.includes("closest") || name.includes("ctp") || name.includes("närmast")) {
    return "closest_to_pin";
  }
  if (name.includes("longest") || name.includes("längsta") || name.includes("long drive")) {
    return "longest_drive";
  }
  return null;
};

const getMiniGameTypeLabel = (gameType) =>
  gameType === "closest_to_pin" ? "Closest to pin" : "Longest drive";

const formatHandicap = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(1);
};

const isValidParList = (values, holeCount) =>
  Array.isArray(values) &&
  values.length === holeCount &&
  values.every(
    (value) =>
      Number.isInteger(Number(value)) &&
      Number(value) >= 2 &&
      Number(value) <= 7
  );

const isValidIndexList = (values, holeCount) => {
  if (!Array.isArray(values) || values.length !== holeCount) return false;

  const normalized = values.map((value) => Number(value));
  return (
    normalized.every(
      (value) => Number.isInteger(value) && value >= 1 && value <= 18
    ) && new Set(normalized).size === holeCount
  );
};

const getCourseHoleSnapshot = (course, roundHoleCount) => {
  if (!course || !roundHoleCount) return null;

  const allHoles = [...(course.holes || [])]
    .filter((hole) => Number.isInteger(Number(hole.hole_number)))
    .sort((a, b) => Number(a.hole_number) - Number(b.hole_number));

  const targetHoles = allHoles.slice(0, roundHoleCount);
  if (targetHoles.length !== roundHoleCount) return null;

  const orderedTees = [...(course.tees || [])].sort(
    (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
  );
  const categories = ["men", "women"];

  for (const tee of orderedTees) {
    for (const category of categories) {
      const rows = (tee.handicaps || []).filter(
        (row) => String(row.rating_category || "") === category
      );
      const byHoleId = new Map(
        rows.map((row) => [String(row.hole_id), row])
      );
      const pars = targetHoles.map((hole) =>
        Number(byHoleId.get(String(hole.id))?.par)
      );
      const indexes = targetHoles.map((hole) =>
        Number(byHoleId.get(String(hole.id))?.stroke_index)
      );

      if (
        isValidParList(pars, roundHoleCount) &&
        isValidIndexList(indexes, roundHoleCount)
      ) {
        return {
          courseName: course.name || "Golfbana",
          clubName: course.golf_clubs?.name || "",
          teeName: tee.name || "",
          category,
          pars,
          indexes,
          usesFrontNine:
            Number(course.hole_count || allHoles.length) > roundHoleCount,
        };
      }
    }
  }

  const allHandicaps = orderedTees.flatMap((tee) => tee.handicaps || []);
  const fallbackRows = targetHoles.map((hole) =>
    allHandicaps.find(
      (item) =>
        String(item.hole_id) === String(hole.id) &&
        Number.isInteger(Number(item.par)) &&
        Number(item.par) >= 2 &&
        Number(item.par) <= 7 &&
        Number.isInteger(Number(item.stroke_index)) &&
        Number(item.stroke_index) >= 1 &&
        Number(item.stroke_index) <= 18
    )
  );
  const pars = fallbackRows.map((row) => Number(row?.par));
  const indexes = fallbackRows.map((row) => Number(row?.stroke_index));

  if (
    !isValidParList(pars, roundHoleCount) ||
    !isValidIndexList(indexes, roundHoleCount)
  ) {
    return null;
  }

  return {
    courseName: course.name || "Golfbana",
    clubName: course.golf_clubs?.name || "",
    teeName: "",
    category: "",
    pars,
    indexes,
    usesFrontNine:
      Number(course.hole_count || allHoles.length) > roundHoleCount,
  };
};

const getHoleSegments = (holeCount) => {
  const normalizedCount = Number(holeCount) === 9 ? 9 : 18;
  const front = Array.from(
    { length: Math.min(9, normalizedCount) },
    (_, index) => index + 1
  );

  if (normalizedCount <= 9) {
    return [{ key: "front", label: "Hål 1–9", holes: front }];
  }

  return [
    { key: "front", label: "Front 9", holes: front },
    {
      key: "back",
      label: "Back 9",
      holes: Array.from({ length: 9 }, (_, index) => index + 10),
    },
  ];
};

const distributeUnitsIntoBalls = (
  sourceUnits,
  targetPeoplePerBall,
  balanceByHandicap,
  playerData = []
) => {
  const targetSize = [2, 3, 4].includes(Number(targetPeoplePerBall))
    ? Number(targetPeoplePerBall)
    : 4;
  const units = Array.isArray(sourceUnits) ? sourceUnits : [];

  if (!balanceByHandicap) {
    let currentBall = 1;
    let peopleInBall = 0;

    return units.map((unit) => {
      const memberCount = Number(unit.memberCount) || 1;

      if (
        peopleInBall > 0 &&
        peopleInBall + memberCount > targetSize
      ) {
        currentBall += 1;
        peopleInBall = 0;
      }

      peopleInBall += memberCount;
      return {
        ...unit,
        ballNumber: currentBall,
      };
    });
  }

  const playerById = new Map(
    (playerData || []).map((player) => [String(player.id), player])
  );
  const totalPeople = units.reduce(
    (sum, unit) => sum + (Number(unit.memberCount) || 1),
    0
  );
  const initialBallCount = Math.max(1, Math.ceil(totalPeople / targetSize));
  const balls = Array.from({ length: initialBallCount }, (_, index) => ({
    ballNumber: index + 1,
    people: 0,
    handicapSum: 0,
  }));

  const rankedUnits = units
    .map((unit, originalIndex) => {
      const playerHandicaps = (Array.isArray(unit.playerIds)
        ? unit.playerIds
        : []
      )
        .map((playerId) => playerById.get(String(playerId)))
        .map((player) => Number(player?.handicap))
        .filter((value) => Number.isFinite(value));
      const memberCount = Number(unit.memberCount) || 1;
      const handicapSum = playerHandicaps.reduce(
        (sum, value) => sum + value,
        0
      );
      const averageHandicap = playerHandicaps.length
        ? handicapSum / playerHandicaps.length
        : Number(unit.fullHandicap) || 0;

      return {
        unit,
        originalIndex,
        memberCount,
        handicapSum:
          playerHandicaps.length > 0
            ? handicapSum
            : averageHandicap * memberCount,
        averageHandicap,
      };
    })
    .sort(
      (a, b) =>
        b.averageHandicap - a.averageHandicap ||
        b.memberCount - a.memberCount ||
        a.originalIndex - b.originalIndex
    );

  const assignmentByKey = new Map();

  rankedUnits.forEach((item) => {
    let candidates = balls.filter(
      (ball) => ball.people + item.memberCount <= targetSize
    );

    if (candidates.length === 0) {
      const newBall = {
        ballNumber: balls.length + 1,
        people: 0,
        handicapSum: 0,
      };
      balls.push(newBall);
      candidates = [newBall];
    }

    const leastPeople = Math.min(...candidates.map((ball) => ball.people));
    const evenlySizedCandidates = candidates.filter(
      (ball) => ball.people === leastPeople
    );

    evenlySizedCandidates.sort((a, b) => {
      const averageA = a.people > 0 ? a.handicapSum / a.people : -Infinity;
      const averageB = b.people > 0 ? b.handicapSum / b.people : -Infinity;
      return averageA - averageB || a.ballNumber - b.ballNumber;
    });

    const targetBall = evenlySizedCandidates[0];
    targetBall.people += item.memberCount;
    targetBall.handicapSum += item.handicapSum;
    assignmentByKey.set(item.unit.clientKey, targetBall.ballNumber);
  });

  return units.map((unit) => ({
    ...unit,
    ballNumber: assignmentByKey.get(unit.clientKey) || 1,
  }));
};

export default function LiveRoundControl({
  tournament,
  theme,
  isAdmin,
}) {
  const [roundRuns, setRoundRuns] = useState({});
  const [roundDrafts, setRoundDrafts] = useState({});
  const [statusLoading, setStatusLoading] =
    useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRoundIndex, setSelectedRoundIndex] =
    useState(0);
  const [units, setUnits] = useState([]);
  const [emailsByBall, setEmailsByBall] = useState(
    {}
  );
  const [existingDetails, setExistingDetails] =
    useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [newEmailsByLiveBall, setNewEmailsByLiveBall] = useState({});
  const [sendingLiveBallId, setSendingLiveBallId] = useState(null);
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const [isPublicLinkBusy, setIsPublicLinkBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] =
    useState("");
  const [copiedToken, setCopiedToken] = useState("");
  const [publicLink, setPublicLink] = useState(null);
  const [copiedPublicLink, setCopiedPublicLink] = useState(false);
  const [miniGameConfigs, setMiniGameConfigs] = useState([]);
  const [parEnabled, setParEnabled] = useState(false);
  const [holePars, setHolePars] = useState([]);
  const [holeIndexes, setHoleIndexes] = useState([]);
  const [courseSnapshot, setCourseSnapshot] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [courseLoading, setCourseLoading] = useState(false);
  const [unitSearch, setUnitSearch] = useState("");
  const [balanceByHandicap, setBalanceByHandicap] = useState(true);
  const [courseInfoEditing, setCourseInfoEditing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    balls: true,
    holeInfo: false,
    miniGames: false,
    invites: false,
  });

  const selectedRound =
    tournament.rounds?.[selectedRoundIndex];
  const hasSelectedCourse = Boolean(selectedCourseId);
  const holeInfoEnabled = hasSelectedCourse || parEnabled;
  const holeSegments = useMemo(
    () => getHoleSegments(selectedRound?.holes ?? 18),
    [selectedRound?.holes]
  );

  const toggleSection = (sectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const supportedMiniGames = useMemo(
    () =>
      (tournament.miniGames || [])
        .map((miniGame) => ({
          ...miniGame,
          liveGameType: getLiveMiniGameType(miniGame),
        }))
        .filter((miniGame) => miniGame.liveGameType),
    [tournament.miniGames]
  );

  const getPlayerPreviousTotal = useCallback(
    (player, roundIndex) => {
      const rawTotal = (player?.scores || [])
        .slice(0, roundIndex)
        .reduce((sum, score) => sum + Number(score || 0), 0);

      const deduction = (tournament.miniGames || []).reduce(
        (total, miniGame) => {
          if (!miniGame.subtract_from_score) return total;

          let wins = 0;
          for (let index = 0; index < roundIndex; index += 1) {
            wins += Number(
              player?.miniGameScores?.[index]?.[miniGame.name] || 0
            );
          }

          return total + wins * Number(miniGame.deduction_value || 0);
        },
        0
      );

      return Math.max(0, Math.round(rawTotal - deduction));
    },
    [tournament.miniGames]
  );

  const fetchRoundRuns = useCallback(async () => {
    if (!isAdmin || !tournament.rounds?.length) {
      setStatusLoading(false);
      return;
    }

    setStatusLoading(true);

    const roundIds = tournament.rounds.map(
      (round) => round.id
    );

    const [runsResponse, draftsResponse] = await Promise.all([
      supabase
        .from("live_rounds")
        .select(
          "id, round_id, status, started_at, completed_at"
        )
        .in("round_id", roundIds)
        .order("started_at", { ascending: false }),
      supabase
        .from("live_round_drafts")
        .select("id, round_id, setup, updated_at")
        .in("round_id", roundIds)
        .order("updated_at", { ascending: false }),
    ]);

    if (runsResponse.error) {
      console.error(
        "Kunde inte hämta live-rundor:",
        runsResponse.error
      );
      setRoundRuns({});
    } else {
      const latestByRound = {};

      (runsResponse.data || []).forEach((run) => {
        if (!latestByRound[run.round_id]) {
          latestByRound[run.round_id] = run;
        }
      });

      setRoundRuns(latestByRound);
    }

    if (draftsResponse.error) {
      console.error(
        "Kunde inte hämta förberedda rundor:",
        draftsResponse.error
      );
      setRoundDrafts({});
    } else {
      setRoundDrafts(
        Object.fromEntries(
          (draftsResponse.data || []).map((draft) => [
            draft.round_id,
            draft,
          ])
        )
      );
    }

    setStatusLoading(false);
  }, [isAdmin, tournament.rounds]);

  useEffect(() => {
    fetchRoundRuns();
  }, [fetchRoundRuns]);

  const pairPlayers = (
    players,
    pairing,
    roundIndex
  ) => {
    const sorted = [...players]
      .map((player) => ({
        ...player,
        previousTotal: (player.scores || [])
          .slice(0, roundIndex)
          .reduce(
            (sum, score) => sum + (score || 0),
            0
          ),
      }))
      .sort((a, b) => {
        if (pairing === "handicap") {
          return (
            Number(a.handicap || 0) -
            Number(b.handicap || 0)
          );
        }

        return a.previousTotal - b.previousTotal;
      });

    const result = [];
    let left = 0;
    let right = sorted.length - 1;

    while (left < right) {
      result.push([
        sorted[left++].id,
        sorted[right--].id,
      ]);
    }

    if (left === right) {
      result.push([sorted[left].id]);
    }

    return result;
  };

  const buildScrambleTeams = (
    round,
    roundIndex
  ) => {
    if (
      Array.isArray(round.teams) &&
      round.teams.length > 0
    ) {
      return round.teams;
    }

    const customTeams =
      round.scrambleOptions?.customTeams;

    if (
      customTeams &&
      Object.keys(customTeams).length > 0
    ) {
      const used = new Set();
      const teams = [];

      Object.entries(customTeams).forEach(
        ([player1, player2]) => {
          if (
            !used.has(player1) &&
            !used.has(player2)
          ) {
            teams.push([player1, player2]);
            used.add(player1);
            used.add(player2);
          }
        }
      );

      if (teams.length > 0) {
        return teams;
      }
    }

    const pairing =
      round.scrambleOptions?.pairing || "position";
    const assignments =
      tournament.team_data?.assignments;

    if (
      assignments &&
      Object.keys(assignments).length > 0
    ) {
      const red = tournament.playerData.filter(
        (player) => assignments[player.id] === "red"
      );
      const green = tournament.playerData.filter(
        (player) =>
          assignments[player.id] === "green"
      );

      return [
        ...pairPlayers(red, pairing, roundIndex),
        ...pairPlayers(green, pairing, roundIndex),
      ];
    }

    return pairPlayers(
      tournament.playerData,
      pairing,
      roundIndex
    );
  };

  const getTeamHandicap = (round, players) => {
    if (
      !round.scrambleOptions?.scrambleWithHandicap ||
      players.length === 0
    ) {
      return 0;
    }

    const handicaps = players.map((player) =>
      Number(player.handicap || 0)
    );
    const low = Math.min(...handicaps);
    const high = Math.max(...handicaps);
    const lowPct =
      Number(round.scrambleOptions.lowPct) || 0;
    const highPct =
      Number(round.scrambleOptions.highPct) || 0;

    return (
      low * (lowPct / 100) +
      high * (highPct / 100)
    );
  };

  const createScoringUnits = (
    round,
    roundIndex
  ) => {
    let rawUnits;

    if (isScrambleRound(round)) {
      const teams = buildScrambleTeams(
        round,
        roundIndex
      );

      rawUnits = teams.map(
        (playerIds, unitIndex) => {
          const players = playerIds
            .map((playerId) =>
              tournament.playerData.find(
                (player) => player.id === playerId
              )
            )
            .filter(Boolean);

          const fullHandicap =
            getTeamHandicap(round, players);
          const roundDeduction =
            (round.holes ?? 18) === 9
              ? Math.round(fullHandicap / 2)
              : Math.round(fullHandicap);

          return {
            clientKey: `unit-${unitIndex + 1}`,
            unitType: "team",
            label:
              players
                .map((player) => player.name)
                .join(" & ") ||
              `Lag ${unitIndex + 1}`,
            playerIds: players.map(
              (player) => player.id
            ),
            playerNames: Object.fromEntries(
              players.map((player) => [player.id, player.name || "Okänd spelare"])
            ),
            playerPreviousTotals: Object.fromEntries(
              players.map((player) => [
                player.id,
                getPlayerPreviousTotal(player, roundIndex),
              ])
            ),
            previousTotal: players.length
              ? Math.round(
                  players.reduce(
                    (sum, player) =>
                      sum + getPlayerPreviousTotal(player, roundIndex),
                    0
                  ) / players.length
                )
              : 0,
            memberCount: players.length,
            fullHandicap,
            roundDeduction,
            ballNumber: 1,
          };
        }
      );
    } else {
      rawUnits = tournament.playerData.map(
        (player, unitIndex) => {
          const fullHandicap = Number(
            player.handicap || 0
          );
          const roundDeduction =
            isHandicapAdjustedRound(round)
              ? (round.holes ?? 18) === 9
                ? Math.round(fullHandicap / 2)
                : Math.round(fullHandicap)
              : 0;

          return {
            clientKey: `unit-${unitIndex + 1}`,
            unitType: "player",
            label: player.name || "Okänd spelare",
            playerIds: [player.id],
            playerNames: {
              [player.id]: player.name || "Okänd spelare",
            },
            playerPreviousTotals: {
              [player.id]: getPlayerPreviousTotal(player, roundIndex),
            },
            previousTotal: getPlayerPreviousTotal(player, roundIndex),
            memberCount: 1,
            fullHandicap,
            roundDeduction,
            ballNumber: 1,
          };
        }
      );
    }

    return distributeUnitsIntoBalls(
      rawUnits,
      4,
      true,
      tournament.playerData
    );
  };

  const fetchExistingDetails = async (run) => {
    setExistingDetails(null);
    setPublicLink(null);
    setErrorMessage("");
    setMessage("");

    const [ballsResponse, miniGamesResponse, publicLinkResponse] =
      await Promise.all([
        supabase
          .from("live_balls")
          .select(
            "id, ball_number, status, submitted_at"
          )
          .eq("live_round_id", run.id)
          .order("ball_number", { ascending: true }),
        supabase
          .from("live_mini_game_holes")
          .select(
            "id, mini_game_id, game_type, mini_game_name, subtract_from_score, deduction_value, hole_number"
          )
          .eq("live_round_id", run.id)
          .order("hole_number", { ascending: true }),
        supabase
          .from("live_round_public_links")
          .select("id, live_round_id, token, created_at")
          .eq("live_round_id", run.id)
          .is("revoked_at", null)
          .maybeSingle(),
      ]);

    if (ballsResponse.error) {
      throw ballsResponse.error;
    }

    if (miniGamesResponse.error) {
      throw miniGamesResponse.error;
    }

    if (publicLinkResponse.error) {
      throw publicLinkResponse.error;
    }

    const balls = ballsResponse.data || [];
    const ballIds = balls.map((ball) => ball.id);
    let invites = [];

    if (ballIds.length > 0) {
      const { data, error } = await supabase
        .from("live_invites")
        .select(
          "id, live_ball_id, email, token, claimed_at, revoked_at"
        )
        .in("live_ball_id", ballIds)
        .order("created_at", { ascending: true });

      if (error) {
        throw error;
      }

      invites = data || [];
    }

    setPublicLink(publicLinkResponse.data || null);
    setExistingDetails({
      run,
      balls,
      invites,
      miniGameHoles: miniGamesResponse.data || [],
    });
  };

  const openRound = async (roundIndex) => {
    setSelectedRoundIndex(roundIndex);
    setMessage("");
    setErrorMessage("");
    setCopiedToken("");
    setPublicLink(null);
    setCopiedPublicLink(false);
    setUnitSearch("");
    setBalanceByHandicap(true);
    setNewEmailsByLiveBall({});
    setSendingLiveBallId(null);
    setResendingInviteId(null);
    setCourseInfoEditing(false);
    setCourseSnapshot(null);
    setSelectedCourseId(null);
    setExpandedSections({
      balls: true,
      holeInfo: false,
      miniGames: supportedMiniGames.length > 0,
      invites: false,
    });

    const round = tournament.rounds[roundIndex];
    const existingRun = roundRuns[round.id];
    const savedDraft = roundDrafts[round.id];
    const draftSetup = savedDraft?.setup || null;
    setBalanceByHandicap(
      typeof draftSetup?.balanceByHandicap === "boolean"
        ? draftSetup.balanceByHandicap
        : true
    );
    const holeCount = Number(round?.holes || 18);

    let nextCourseSnapshot = null;
    let courseId = round?.course_id || null;

    if (!courseId && round?.id) {
      const { data: roundCourseRow, error: roundCourseError } = await supabase
        .from("rounds")
        .select("course_id")
        .eq("id", round.id)
        .maybeSingle();

      if (!roundCourseError) {
        courseId = roundCourseRow?.course_id || null;
      }
    }

    setSelectedCourseId(courseId || null);

    if (courseId) {
      setCourseLoading(true);
      try {
        const course = await fetchGolfCourseDetails(courseId);
        nextCourseSnapshot = getCourseHoleSnapshot(course, holeCount);
        setCourseSnapshot(nextCourseSnapshot);
      } catch (error) {
        console.warn(
          "Kunde inte hämta baninformationen till live-rundan:",
          error
        );
        setCourseSnapshot(null);
      } finally {
        setCourseLoading(false);
      }
    } else {
      setCourseLoading(false);
    }

    const savedPars = Array.isArray(round?.hole_pars)
      ? round.hole_pars.map((value) => Number(value))
      : [];
    const savedIndexes = Array.isArray(round?.hole_indexes)
      ? round.hole_indexes.map((value) => Number(value))
      : [];
    const hasValidSavedPars = isValidParList(savedPars, holeCount);
    const hasValidSavedIndexes = isValidIndexList(savedIndexes, holeCount);
    const coursePars = nextCourseSnapshot?.pars || [];
    const courseIndexes = nextCourseSnapshot?.indexes || [];
    const hasValidCoursePars = isValidParList(coursePars, holeCount);
    const hasValidCourseIndexes = isValidIndexList(courseIndexes, holeCount);
    const defaultIndexes = Array.from(
      { length: holeCount },
      (_, index) => index + 1
    );
    const fallbackPars = courseId && hasValidCoursePars
      ? coursePars
      : hasValidSavedPars
        ? savedPars
        : Array.from({ length: holeCount }, () => 4);
    const fallbackIndexes = courseId && hasValidCourseIndexes
      ? courseIndexes
      : hasValidSavedIndexes
        ? savedIndexes
        : defaultIndexes;
    const fallbackParEnabled = courseId
      ? true
      : hasValidSavedPars || hasValidSavedIndexes;

    const defaultMiniGameConfigs = supportedMiniGames.map(
      (miniGame) => ({
        miniGameId: miniGame.id,
        name: miniGame.name,
        gameType: miniGame.liveGameType,
        selectedHoles: [],
      })
    );

    setModalOpen(true);

    if (existingRun) {
      setParEnabled(courseId ? true : fallbackParEnabled);
      setHolePars(fallbackPars);
      setHoleIndexes(fallbackIndexes);
      setMiniGameConfigs(defaultMiniGameConfigs);

      try {
        await fetchExistingDetails(existingRun);
      } catch (error) {
        console.error(
          "Kunde inte hämta live-rundan:",
          error
        );
        setErrorMessage(
          "Live-rundan kunde inte hämtas."
        );
      }

      return;
    }

    const createdUnits = createScoringUnits(
      round,
      roundIndex
    );
    const draftUnits = Array.isArray(draftSetup?.units)
      ? draftSetup.units
      : createdUnits;
    const draftMiniGames = Array.isArray(
      draftSetup?.miniGameConfigs
    )
      ? draftSetup.miniGameConfigs
      : [];
    const restoredMiniGameConfigs = defaultMiniGameConfigs.map(
      (config) => {
        const savedConfig = draftMiniGames.find(
          (item) =>
            String(item.miniGameId) ===
            String(config.miniGameId)
        );

        return {
          ...config,
          selectedHoles: Array.isArray(
            savedConfig?.selectedHoles
          )
            ? savedConfig.selectedHoles
                .map((value) => Number(value))
                .filter(
                  (value) =>
                    Number.isInteger(value) &&
                    value >= 1 &&
                    value <= holeCount
                )
            : [],
        };
      }
    );
    const draftPars = Array.isArray(draftSetup?.holePars)
      ? draftSetup.holePars.map((value) => Number(value))
      : [];
    const draftIndexes = Array.isArray(draftSetup?.holeIndexes)
      ? draftSetup.holeIndexes.map((value) => Number(value))
      : [];
    const hasValidDraftPars = isValidParList(draftPars, holeCount);
    const hasValidDraftIndexes = isValidIndexList(draftIndexes, holeCount);
    const restoredParEnabled = Boolean(
      draftSetup?.parEnabled && hasValidDraftPars && hasValidDraftIndexes
    );

    setExistingDetails(null);
    setUnits(draftUnits);
    setMiniGameConfigs(restoredMiniGameConfigs);
    setParEnabled(
      courseId ? true : draftSetup ? restoredParEnabled : fallbackParEnabled
    );
    setHolePars(
      draftSetup && hasValidDraftPars
        ? draftPars
        : fallbackPars
    );
    setHoleIndexes(
      draftSetup && hasValidDraftIndexes
        ? draftIndexes
        : fallbackIndexes
    );

    const ballNumbers = [
      ...new Set(
        draftUnits.map(
          (unit) => Number(unit.ballNumber) || 1
        )
      ),
    ];
    const savedEmails =
      draftSetup?.emailsByBall &&
      typeof draftSetup.emailsByBall === "object"
        ? draftSetup.emailsByBall
        : {};

    setEmailsByBall(
      Object.fromEntries(
        ballNumbers.map((ballNumber) => [
          ballNumber,
          savedEmails[ballNumber] ||
            savedEmails[String(ballNumber)] ||
            "",
        ])
      )
    );

    if (savedDraft) {
      setMessage(
        "Den sparade förberedelsen har öppnats."
      );
    }
  };

  const closeModal = () => {
    if (
      isStarting ||
      isSavingDraft ||
      isSending ||
      sendingLiveBallId ||
      resendingInviteId ||
      isPublicLinkBusy
    ) {
      return;
    }

    setModalOpen(false);
    setExistingDetails(null);
    setPublicLink(null);
    setCopiedPublicLink(false);
    setMessage("");
    setErrorMessage("");
    setMiniGameConfigs([]);
    setParEnabled(false);
    setHolePars([]);
    setHoleIndexes([]);
    setCourseSnapshot(null);
    setSelectedCourseId(null);
    setCourseLoading(false);
    setUnitSearch("");
    setNewEmailsByLiveBall({});
    setSendingLiveBallId(null);
    setResendingInviteId(null);
    setCourseInfoEditing(false);
    setExpandedSections({
      balls: true,
      holeInfo: false,
      miniGames: false,
      invites: false,
    });
  };

  const usedBalls = useMemo(() => {
    const map = new Map();

    units.forEach((unit) => {
      const ballNumber = Number(unit.ballNumber) || 1;
      const existing = map.get(ballNumber) || {
        ballNumber,
        units: [],
        people: 0,
      };

      existing.units.push(unit);
      existing.people += Number(unit.memberCount) || 1;
      map.set(ballNumber, existing);
    });

    return [...map.values()].sort(
      (a, b) => a.ballNumber - b.ballNumber
    );
  }, [units]);

  const totalPeople = useMemo(
    () =>
      units.reduce(
        (sum, unit) => sum + (Number(unit.memberCount) || 1),
        0
      ),
    [units]
  );

  const getBallAverageHandicap = (ball) => {
    const playerIds = [
      ...new Set(
        (ball?.units || []).flatMap((unit) =>
          Array.isArray(unit.playerIds) ? unit.playerIds : []
        )
      ),
    ];

    const handicaps = playerIds
      .map((playerId) =>
        tournament.playerData?.find((player) => player.id === playerId)
      )
      .map((player) => Number(player?.handicap))
      .filter((handicap) => Number.isFinite(handicap));

    if (handicaps.length === 0) return null;

    return (
      handicaps.reduce((sum, handicap) => sum + handicap, 0) /
      handicaps.length
    );
  };

  const ballsMissingEmails = useMemo(
    () =>
      usedBalls.filter(
        (ball) => splitEmails(emailsByBall[ball.ballNumber]).length === 0
      ),
    [usedBalls, emailsByBall]
  );

  const ballsWithInvalidEmails = useMemo(
    () =>
      usedBalls.filter((ball) => {
        const emails = splitEmails(emailsByBall[ball.ballNumber]);
        return (
          emails.length > ball.people ||
          emails.some((email) => !EMAIL_PATTERN.test(email))
        );
      }),
    [usedBalls, emailsByBall]
  );

  const allBallsHaveEmails =
    usedBalls.length > 0 && ballsMissingEmails.length === 0;
  const allBallsReadyForStart =
    allBallsHaveEmails && ballsWithInvalidEmails.length === 0;

  const nextBallNumber = useMemo(
    () =>
      usedBalls.length > 0
        ? Math.max(...usedBalls.map((ball) => ball.ballNumber)) + 1
        : 1,
    [usedBalls]
  );

  const filteredBalls = useMemo(() => {
    const query = unitSearch.trim().toLocaleLowerCase("sv-SE");
    if (!query) return usedBalls;

    return usedBalls.filter((ball) =>
      ball.units.some((unit) => {
        const names = Object.values(unit.playerNames || {});
        return [unit.label, ...names]
          .filter(Boolean)
          .some((value) =>
            String(value)
              .toLocaleLowerCase("sv-SE")
              .includes(query)
          );
      })
    );
  }, [unitSearch, usedBalls]);

  const updateUnitBall = (clientKey, ballNumber) => {
    setUnits((current) =>
      current.map((unit) =>
        unit.clientKey === clientKey
          ? {
              ...unit,
              ballNumber,
            }
          : unit
      )
    );

    setEmailsByBall((current) => ({
      ...current,
      [ballNumber]: current[ballNumber] || "",
    }));
  };

  const moveUnitByOffset = (unit, offset) => {
    const numbers = usedBalls.map((ball) => ball.ballNumber);
    const currentIndex = numbers.indexOf(Number(unit.ballNumber) || 1);
    if (currentIndex < 0) return;

    let targetBallNumber;
    if (offset < 0) {
      if (currentIndex === 0) return;
      targetBallNumber = numbers[currentIndex - 1];
    } else if (currentIndex === numbers.length - 1) {
      targetBallNumber = nextBallNumber;
    } else {
      targetBallNumber = numbers[currentIndex + 1];
    }

    updateUnitBall(unit.clientKey, targetBallNumber);
  };

  const autoDistributeBalls = (targetPeoplePerBall) => {
    setUnits((current) =>
      distributeUnitsIntoBalls(
        current,
        targetPeoplePerBall,
        balanceByHandicap,
        tournament.playerData
      )
    );

    setEmailsByBall({});
    setMessage(
      balanceByHandicap
        ? `Deltagarna har fördelats i ${targetPeoplePerBall}-boll och balanserats efter handicap. Lägg därefter in e-postadresser för de nya bollarna.`
        : `Deltagarna har fördelats i ${targetPeoplePerBall}-boll i befintlig deltagarordning. Lägg därefter in e-postadresser för de nya bollarna.`
    );
  };

  const parThreeHoles = useMemo(
    () =>
      holeInfoEnabled
        ? holePars
            .map((value, index) =>
              Number(value) === 3 ? index + 1 : null
            )
            .filter(Boolean)
        : [],
    [holePars, holeInfoEnabled]
  );

  const hasCourseOverrides = useMemo(() => {
    if (!courseSnapshot) return false;

    return (
      courseSnapshot.pars.some(
        (value, index) => Number(holePars[index]) !== Number(value)
      ) ||
      courseSnapshot.indexes.some(
        (value, index) => Number(holeIndexes[index]) !== Number(value)
      )
    );
  }, [courseSnapshot, holePars, holeIndexes]);

  const selectAllParThrees = (miniGameId) => {
    if (parThreeHoles.length === 0) return;

    setMiniGameConfigs((current) =>
      current.map((config) =>
        config.miniGameId === miniGameId
          ? {
              ...config,
              selectedHoles: [...parThreeHoles],
            }
          : config
      )
    );
  };

  const toggleMiniGameHole = (miniGameId, holeNumber) => {
    setMiniGameConfigs((current) =>
      current.map((config) => {
        if (config.miniGameId !== miniGameId) return config;

        const selected = config.selectedHoles.includes(holeNumber);
        return {
          ...config,
          selectedHoles: selected
            ? config.selectedHoles.filter((hole) => hole !== holeNumber)
            : [...config.selectedHoles, holeNumber].sort((a, b) => a - b),
        };
      })
    );
  };

  const updateHolePar = (holeIndex, rawValue) => {
    const parsed = Number.parseInt(rawValue, 10);

    setHolePars((current) =>
      current.map((value, index) =>
        index === holeIndex
          ? Number.isFinite(parsed)
            ? parsed
            : ""
          : value
      )
    );
  };

  const updateHoleIndex = (holeIndex, rawValue) => {
    const parsed = Number.parseInt(rawValue, 10);

    setHoleIndexes((current) =>
      current.map((value, index) =>
        index === holeIndex
          ? Number.isFinite(parsed)
            ? parsed
            : ""
          : value
      )
    );
  };

  const getLiveLink = (token) =>
    `${window.location.origin}/live-scorekort/${token}`;

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(
        getLiveLink(token)
      );
      setCopiedToken(token);

      window.setTimeout(() => {
        setCopiedToken("");
      }, 1800);
    } catch (error) {
      console.error(
        "Kunde inte kopiera länken:",
        error
      );
      setErrorMessage(
        "Länken kunde inte kopieras automatiskt."
      );
    }
  };

  const getPublicViewerLink = (viewerToken) =>
    `${window.location.origin}/live-resultat/${viewerToken}`;

  const copyPublicViewerLink = async () => {
    if (!publicLink?.token) return;

    try {
      await navigator.clipboard.writeText(
        getPublicViewerLink(publicLink.token)
      );
      setCopiedPublicLink(true);
      window.setTimeout(() => {
        setCopiedPublicLink(false);
      }, 1800);
    } catch (error) {
      console.error(
        "Kunde inte kopiera den publika länken:",
        error
      );
      setErrorMessage(
        "Tittarlänken kunde inte kopieras automatiskt."
      );
    }
  };

  const handleCreatePublicLink = async () => {
    if (!existingDetails?.run?.id) return;

    setIsPublicLinkBusy(true);
    setErrorMessage("");
    setMessage("");

    try {
      const { data, error } = await supabase.rpc(
        "create_or_get_live_round_public_link",
        {
          p_live_round_id: existingDetails.run.id,
        }
      );

      if (error) throw error;
      if (!data?.token) {
        throw new Error("Tittarlänken kunde inte skapas.");
      }

      setPublicLink({
        live_round_id: existingDetails.run.id,
        token: data.token,
        created_at: data.created_at,
      });
      setMessage(
        "En skrivskyddad tittarlänk har skapats."
      );
    } catch (error) {
      console.error(
        "Kunde inte skapa publik tittarlänk:",
        error
      );
      setErrorMessage(
        error.message ||
          "Tittarlänken kunde inte skapas."
      );
    } finally {
      setIsPublicLinkBusy(false);
    }
  };

  const handleRevokePublicLink = async () => {
    if (!existingDetails?.run?.id) return;

    setIsPublicLinkBusy(true);
    setErrorMessage("");
    setMessage("");

    try {
      const { error } = await supabase.rpc(
        "revoke_live_round_public_link",
        {
          p_live_round_id: existingDetails.run.id,
        }
      );

      if (error) throw error;

      setPublicLink(null);
      setCopiedPublicLink(false);
      setMessage(
        "Tittarlänken har stängts av."
      );
    } catch (error) {
      console.error(
        "Kunde inte stänga tittarlänken:",
        error
      );
      setErrorMessage(
        error.message ||
          "Tittarlänken kunde inte stängas av."
      );
    } finally {
      setIsPublicLinkBusy(false);
    }
  };

  const sendInvitations = async (
    liveRoundId,
    invitations
  ) => {
    const invitationIds = invitations
      .map(
        (invitation) =>
          invitation.invitation_id ||
          invitation.id
      )
      .filter(Boolean);

    if (invitationIds.length === 0) {
      throw new Error(
        "Det finns inga inbjudningar att skicka."
      );
    }

    const { error } =
      await supabase.functions.invoke(
        "send-live-scorecard-invites",
        {
          body: {
            liveRoundId,
            invitationIds,
          },
        }
      );

    if (error) {
      throw error;
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedRound || existingDetails) {
      return;
    }

    setErrorMessage("");
    setMessage("");

    if (units.length === 0) {
      setErrorMessage(
        "Det finns inga spelare eller lag att placera."
      );
      return;
    }

    const expectedHoleCount = Number(
      selectedRound?.holes || 18
    );
    const normalizedHolePars = holePars.map((value) => Number(value));
    const normalizedHoleIndexes = holeIndexes.map((value) => Number(value));

    if (holeInfoEnabled && !isValidParList(normalizedHolePars, expectedHoleCount)) {
      setErrorMessage(
        `Fyll i ett giltigt par mellan 2 och 7 för samtliga ${expectedHoleCount} hål.`
      );
      return;
    }

    if (holeInfoEnabled && !isValidIndexList(normalizedHoleIndexes, expectedHoleCount)) {
      setErrorMessage(
        `Fyll i ett unikt hålindex mellan 1 och 18 för samtliga ${expectedHoleCount} hål. Samma index får inte användas två gånger.`
      );
      return;
    }

    for (const ball of usedBalls) {
      if (ball.people > 4) {
        setErrorMessage(
          `Boll ${ball.ballNumber} innehåller ${ball.people} personer. Högst fyra är tillåtet.`
        );
        return;
      }

      const emails = splitEmails(
        emailsByBall[ball.ballNumber]
      );

      if (emails.length > ball.people) {
        setErrorMessage(
          `Boll ${ball.ballNumber} har ${ball.people} deltagare och kan därför ha högst ${ball.people} e-postadresser.`
        );
        return;
      }

      const invalidEmail = emails.find(
        (email) => !EMAIL_PATTERN.test(email)
      );

      if (invalidEmail) {
        setErrorMessage(
          `${invalidEmail} är inte en giltig e-postadress.`
        );
        return;
      }
    }

    setIsSavingDraft(true);

    try {
      const setup = {
        version: 2,
        units,
        emailsByBall,
        miniGameConfigs,
        parEnabled: hasSelectedCourse ? true : parEnabled,
        holePars: holeInfoEnabled ? normalizedHolePars : [],
        holeIndexes: holeInfoEnabled ? normalizedHoleIndexes : [],
        balanceByHandicap,
      };

      const { data, error } = await supabase
        .from("live_round_drafts")
        .upsert(
          {
            round_id: selectedRound.id,
            tournament_id: tournament.id,
            setup,
          },
          {
            onConflict: "round_id",
          }
        )
        .select("id, round_id, setup, updated_at")
        .single();

      if (error) throw error;

      setRoundDrafts((current) => ({
        ...current,
        [selectedRound.id]: data,
      }));
      setMessage(
        "Förberedelserna är sparade. Du kan stänga och starta rundan senare."
      );
    } catch (error) {
      console.error(
        "Kunde inte spara rundförberedelsen:",
        error
      );
      setErrorMessage(
        error.message ||
          "Förberedelserna kunde inte sparas."
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleStartRound = async () => {
    if (!selectedRound) {
      return;
    }

    setErrorMessage("");
    setMessage("");

    if (units.length === 0) {
      setErrorMessage(
        "Det finns inga spelare eller lag att placera."
      );
      return;
    }

    const expectedHoleCount = Number(selectedRound?.holes || 18);
    const normalizedHolePars = holePars.map((value) => Number(value));
    const normalizedHoleIndexes = holeIndexes.map((value) => Number(value));

    if (holeInfoEnabled && !isValidParList(normalizedHolePars, expectedHoleCount)) {
      setErrorMessage(
        `Fyll i ett giltigt par mellan 2 och 7 för samtliga ${expectedHoleCount} hål.`
      );
      return;
    }

    if (holeInfoEnabled && !isValidIndexList(normalizedHoleIndexes, expectedHoleCount)) {
      setErrorMessage(
        `Fyll i ett unikt hålindex mellan 1 och 18 för samtliga ${expectedHoleCount} hål. Samma index får inte användas två gånger.`
      );
      return;
    }

    for (const ball of usedBalls) {
      if (ball.people > 4) {
        setErrorMessage(
          `Boll ${ball.ballNumber} innehåller ${ball.people} personer. Högst fyra är tillåtet.`
        );
        return;
      }

      const emails = splitEmails(
        emailsByBall[ball.ballNumber]
      );

      if (emails.length === 0) {
        setErrorMessage(
          `Lägg till minst en e-postadress för boll ${ball.ballNumber}.`
        );
        return;
      }

      if (emails.length > ball.people) {
        setErrorMessage(
          `Boll ${ball.ballNumber} har ${ball.people} deltagare och kan därför ha högst ${ball.people} e-postadresser.`
        );
        return;
      }

      const invalidEmail = emails.find(
        (email) => !EMAIL_PATTERN.test(email)
      );

      if (invalidEmail) {
        setErrorMessage(
          `${invalidEmail} är inte en giltig e-postadress.`
        );
        return;
      }
    }

    setIsStarting(true);

    try {
      const ballsPayload = usedBalls.map(
        (ball) => ({
          client_key: `ball-${ball.ballNumber}`,
          ball_number: ball.ballNumber,
        })
      );

      const unitsPayload = units.map(
        (unit, index) => ({
          ball_key: `ball-${unit.ballNumber}`,
          unit_type: unit.unitType,
          label: unit.label,
          player_ids: unit.playerIds,
          sort_order: index,
          full_handicap: unit.fullHandicap,
          round_deduction: unit.roundDeduction,
          previous_total: unit.previousTotal,
          player_names: unit.playerNames,
          player_previous_totals: unit.playerPreviousTotals,
        })
      );

      const miniGamesPayload = miniGameConfigs.flatMap((config) =>
        config.selectedHoles.map((holeNumber) => ({
          mini_game_id: config.miniGameId,
          game_type: config.gameType,
          hole_number: holeNumber,
        }))
      );

      const invitesPayload = usedBalls.flatMap(
        (ball) =>
          splitEmails(
            emailsByBall[ball.ballNumber]
          ).map((email) => ({
            ball_key: `ball-${ball.ballNumber}`,
            email,
            token: createInviteToken(),
          }))
      );

      const { data, error } = await supabase.rpc(
        "start_live_round_with_mini_games",
        {
          p_round_id: selectedRound.id,
          p_balls: ballsPayload,
          p_units: unitsPayload,
          p_invites: invitesPayload,
          p_mini_games: miniGamesPayload,
          p_hole_pars: holeInfoEnabled ? normalizedHolePars : null,
          p_hole_indexes: holeInfoEnabled ? normalizedHoleIndexes : null,
        }
      );

      if (error) {
        throw error;
      }

      const newRun = {
        id: data.live_round_id,
        round_id: selectedRound.id,
        status: "active",
        started_at: new Date().toISOString(),
      };

      setRoundRuns((current) => ({
        ...current,
        [selectedRound.id]: newRun,
      }));

      const { error: draftDeleteError } = await supabase
        .from("live_round_drafts")
        .delete()
        .eq("round_id", selectedRound.id);

      if (draftDeleteError) {
        console.warn(
          "Live-rundan startades men utkastet kunde inte rensas:",
          draftDeleteError
        );
      }

      setRoundDrafts((current) => {
        const next = { ...current };
        delete next[selectedRound.id];
        return next;
      });

      await fetchExistingDetails(newRun);

      try {
        await sendInvitations(
          data.live_round_id,
          data.invites
        );
        setMessage(
          "Live-rundan är startad och länkarna har skickats."
        );
      } catch (emailError) {
        console.error(
          "Rundan startades men mejlen kunde inte skickas:",
          emailError
        );
        setMessage(
          "Live-rundan är startad. Mejlen kunde inte skickas, men länkarna kan kopieras nedan."
        );
      }

      await fetchRoundRuns();
    } catch (error) {
      console.error(
        "Kunde inte starta live-rundan:",
        error
      );
      setErrorMessage(
        error.message ||
          "Live-rundan kunde inte startas."
      );
    } finally {
      setIsStarting(false);
    }
  };

  const handleResend = async () => {
    if (!existingDetails?.invites?.length) {
      return;
    }

    setIsSending(true);
    setMessage("");
    setErrorMessage("");

    try {
      await sendInvitations(
        existingDetails.run.id,
        existingDetails.invites
      );
      setMessage("Länkarna har skickats igen.");
    } catch (error) {
      console.error(
        "Kunde inte skicka länkarna:",
        error
      );
      setErrorMessage(
        "Mejlen kunde inte skickas. Kontrollera Edge Function och Resend-inställningarna."
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleResendInvite = async (invite) => {
    if (!existingDetails?.run?.id || !invite?.id) return;

    setResendingInviteId(invite.id);
    setMessage("");
    setErrorMessage("");

    try {
      await sendInvitations(existingDetails.run.id, [invite]);
      setMessage(`Scorekortslänken har skickats igen till ${invite.email}.`);
    } catch (error) {
      console.error("Kunde inte skicka länken igen:", error);
      setErrorMessage(
        error.message || "Länken kunde inte skickas igen."
      );
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleSendNewInvites = async (ball, currentInvites = []) => {
    if (!existingDetails?.run?.id || !ball?.id) return;

    const emails = splitEmails(newEmailsByLiveBall[ball.id] || "");

    if (emails.length === 0) {
      setErrorMessage(`Skriv minst en e-postadress för boll ${ball.ball_number}.`);
      return;
    }

    const invalidEmail = emails.find((email) => !EMAIL_PATTERN.test(email));
    if (invalidEmail) {
      setErrorMessage(`${invalidEmail} är inte en giltig e-postadress.`);
      return;
    }

    const existingEmailSet = new Set(
      currentInvites
        .filter((invite) => !invite.revoked_at)
        .map((invite) => String(invite.email || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const alreadySent = emails.filter((email) => existingEmailSet.has(email));

    if (alreadySent.length > 0) {
      setErrorMessage(
        `${alreadySent.join(", ")} har redan fått en scorekortslänk för boll ${ball.ball_number}. Använd “Skicka igen” vid adressen ovan om du vill skicka samma länk på nytt.`
      );
      return;
    }

    setSendingLiveBallId(ball.id);
    setMessage("");
    setErrorMessage("");

    try {
      const { data, error } = await supabase.rpc(
        "create_live_ball_invites",
        {
          p_live_ball_id: ball.id,
          p_emails: emails,
        }
      );

      if (error) throw error;

      const createdInvites = Array.isArray(data)
        ? data
        : Array.isArray(data?.invites)
          ? data.invites
          : [];

      if (createdInvites.length === 0) {
        throw new Error("Inga nya scorekortslänkar kunde skapas.");
      }

      let emailSendFailed = false;
      try {
        await sendInvitations(existingDetails.run.id, createdInvites);
      } catch (emailError) {
        emailSendFailed = true;
        console.error(
          "Nya länkar skapades men mejlen kunde inte skickas:",
          emailError
        );
      }

      await fetchExistingDetails(existingDetails.run);
      setNewEmailsByLiveBall((current) => ({
        ...current,
        [ball.id]: "",
      }));

      if (emailSendFailed) {
        setMessage(
          "De nya scorekortslänkarna skapades, men mejlet kunde inte skickas. Du kan skicka länken igen från mottagarlistan."
        );
      } else {
        setMessage(
          `${createdInvites.length} ${createdInvites.length === 1 ? "ny scorekortslänk har" : "nya scorekortslänkar har"} skickats för boll ${ball.ball_number}.`
        );
      }
    } catch (error) {
      console.error("Kunde inte lägga till nya mottagare:", error);
      setErrorMessage(
        error.message || "Nya mottagare kunde inte läggas till."
      );
    } finally {
      setSendingLiveBallId(null);
    }
  };

  const handleReopenBall = async (ball) => {
    setErrorMessage("");
    setMessage("");

    try {
      const { error } = await supabase.rpc(
        "reopen_live_ball",
        {
          p_ball_id: ball.id,
        }
      );

      if (error) {
        throw error;
      }

      setExistingDetails((current) => ({
        ...current,
        run: {
          ...current.run,
          status: "active",
          completed_at: null,
        },
        balls: current.balls.map((item) =>
          item.id === ball.id
            ? {
                ...item,
                status: "active",
                submitted_at: null,
              }
            : item
        ),
      }));

      setRoundRuns((current) => ({
        ...current,
        [selectedRound.id]: {
          ...current[selectedRound.id],
          status: "active",
          completed_at: null,
        },
      }));

      setMessage(
        `Boll ${ball.ball_number} är öppen för redigering igen.`
      );
    } catch (error) {
      console.error(
        "Kunde inte öppna bollen igen:",
        error
      );
      setErrorMessage(
        error.message ||
          "Bollen kunde inte öppnas igen."
      );
    }
  };

  const statusLabel = (status) => {
    if (status === "completed") {
      return "Avslutad";
    }

    if (status === "cancelled") {
      return "Avbruten";
    }

    return "Live";
  };

  if (!isAdmin || !tournament.rounds?.length) {
    return null;
  }


  const renderHoleInformationGrid = ({
    editable = false,
    showCourseBaseline = false,
  } = {}) => (
    <div className="space-y-3">
      {holeSegments.map((segment) => (
        <section key={`hole-info-${segment.key}`}>
          <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
            <p
              className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.17em]`}
            >
              {segment.label}
            </p>
            <span className={`${theme.textSecondary} text-[8px] font-medium opacity-70`}>
              {segment.holes.length} hål
            </span>
          </div>

          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-9 sm:gap-2">
            {segment.holes.map((holeNumber) => {
              const holeIndex = holeNumber - 1;
              const currentPar = holePars[holeIndex] ?? "";
              const currentIndex = holeIndexes[holeIndex] ?? "";
              const ordinaryPar = courseSnapshot?.pars?.[holeIndex];
              const ordinaryIndex = courseSnapshot?.indexes?.[holeIndex];
              const parChanged = Boolean(
                showCourseBaseline &&
                  courseSnapshot &&
                  Number(currentPar) !== Number(ordinaryPar)
              );
              const indexChanged = Boolean(
                showCourseBaseline &&
                  courseSnapshot &&
                  Number(currentIndex) !== Number(ordinaryIndex)
              );
              const changed = parChanged || indexChanged;

              return (
                <article
                  key={`hole-card-${holeNumber}`}
                  className={`${theme.section} ${
                    changed ? "border-[#d7b96e]" : theme.border
                  } overflow-hidden rounded-lg border transition-colors sm:rounded-xl`}
                >
                  <div className="relative px-1.5 pb-1.5 pt-3.5 text-center sm:px-2 sm:pb-2 sm:pt-4">
                    <span
                      className={`${theme.textSecondary} absolute left-1.5 top-1 text-[8px] font-semibold tabular-nums opacity-65 sm:left-2 sm:top-1.5`}
                      aria-label={`Hål ${holeNumber}`}
                    >
                      {holeNumber}
                    </span>

                    <span
                      className={`${theme.textSecondary} block text-[6px] font-semibold uppercase tracking-[0.16em] sm:text-[7px]`}
                    >
                      Par
                    </span>

                    {editable ? (
                      <input
                        type="number"
                        inputMode="numeric"
                        min="2"
                        max="7"
                        value={currentPar}
                        onChange={(event) =>
                          updateHolePar(holeIndex, event.target.value)
                        }
                        className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} mx-auto mt-0.5 h-7 w-full max-w-[42px] rounded-md border px-1 text-center text-sm font-semibold tabular-nums outline-none focus:ring-2 focus:ring-[#183d2d]/15`}
                        aria-label={`Par för hål ${holeNumber}`}
                      />
                    ) : (
                      <span
                        className={`mt-0.5 block text-[15px] font-semibold leading-none tabular-nums sm:text-base ${
                          parChanged ? "text-[#8a6623]" : theme.textPrimary
                        }`}
                      >
                        {currentPar || "–"}
                      </span>
                    )}

                    {parChanged && (
                      <span className="mt-0.5 block text-[6px] font-medium leading-none text-[#9a752d]">
                        ord. {ordinaryPar}
                      </span>
                    )}
                  </div>

                  <div
                    className={`${theme.sectionMuted} ${theme.border} flex min-h-[24px] items-center justify-center gap-1 border-t px-1 py-1 text-center`}
                  >
                    <span
                      className={`${theme.textSecondary} text-[6px] font-semibold uppercase tracking-[0.12em] opacity-60`}
                    >
                      Index
                    </span>

                    {editable ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={2}
                        value={currentIndex}
                        onChange={(event) =>
                          updateHoleIndex(holeIndex, event.target.value)
                        }
                        className={`${theme.section} ${theme.border} ${theme.textSecondary} h-6 w-[40px] rounded-md border px-1 text-center text-[10px] font-medium tabular-nums outline-none focus:border-[#183d2d]/30 focus:ring-1 focus:ring-[#183d2d]/10`}
                        aria-label={`Hålindex för hål ${holeNumber}`}
                      />
                    ) : (
                      <span
                        className={`text-[10px] font-medium leading-none tabular-nums ${
                          indexChanged ? "text-[#9a752d]" : theme.textSecondary
                        }`}
                      >
                        {currentIndex || "–"}
                      </span>
                    )}

                    {indexChanged && (
                      <span className="text-[6px] font-medium leading-none text-[#a78645]">
                        ({ordinaryIndex})
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  return (
    <>
      <section
        className={`${theme.section} ${theme.border} mt-4 overflow-hidden rounded-2xl border shadow-sm sm:mt-5`}
      >
        <div
          className={`${theme.border} flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5`}
        >
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`${theme.primarySoft} ${theme.heading} flex h-9 w-9 items-center justify-center rounded-xl`}
              >
                <PlayCircleIcon className="h-4 w-4" />
              </span>

              <div>
                <p
                  className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.2em]`}
                >
                  Digitalt scorekort
                </p>
                <h2
                  className={`${theme.heading} mt-0.5 text-base font-semibold`}
                >
                  Förbered, starta och hantera live-rundor
                </h2>
              </div>
            </div>

            <p
              className={`${theme.textSecondary} mt-2 max-w-2xl text-xs leading-5`}
            >
              Förbered par, bollar och sidotävlingar i förväg. Starta sedan
              rundan när det är dags och dela live-resultaten med spelare och
              åskådare.
            </p>
          </div>

          {statusLoading && (
            <span
              className={`${theme.textSecondary} inline-flex items-center gap-2 text-xs`}
            >
              <Loader2Icon className="h-4 w-4 animate-spin" />
              Kontrollerar status…
            </span>
          )}
        </div>

        <div className="grid gap-3 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-3">
          {tournament.rounds.map(
            (round, roundIndex) => {
              const run = roundRuns[round.id];
              const isActive =
                run?.status === "active";
              const isCompleted =
                run?.status === "completed";
              const draft = roundDrafts[round.id];
              const isPrepared = !run && Boolean(draft);

              return (
                <article
                  key={round.id}
                  className={`${theme.tableRow} ${theme.border} rounded-xl border p-3.5`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p
                        className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}
                      >
                        Runda {roundIndex + 1}
                      </p>
                      <h3
                        className={`${theme.textPrimary} mt-0.5 truncate text-sm font-semibold`}
                        title={round.name}
                      >
                        {round.name ||
                          `Runda ${roundIndex + 1}`}
                      </h3>
                      <p
                        className={`${theme.textSecondary} mt-1 truncate text-[10px]`}
                      >
                        {round.modes?.[0] ||
                          "Spelform saknas"}{" "}
                        · {round.holes ?? 18} hål
                      </p>
                    </div>

                    {(run || isPrepared) && (
                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-semibold ${
                          isPrepared
                            ? `${theme.accentSoft} ${theme.accent}`
                            : isActive
                              ? `${theme.primarySoft} ${theme.positive}`
                              : isCompleted
                                ? `${theme.accentSoft} ${theme.accent}`
                                : `${theme.sectionMuted} ${theme.textSecondary}`
                        }`}
                      >
                        {isPrepared
                          ? "Förberedd"
                          : statusLabel(run.status)}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      openRound(roundIndex)
                    }
                    className={`mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                      run || isPrepared
                        ? `${theme.sectionMuted} ${theme.border} ${theme.heading} border hover:bg-black/[0.025]`
                        : `${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary}`
                    }`}
                  >
                    {run ? (
                      <>
                        <LinkIcon className="h-4 w-4" />
                        Hantera live-runda
                      </>
                    ) : isPrepared ? (
                      <>
                        <SaveIcon className="h-4 w-4" />
                        Fortsätt förbereda
                      </>
                    ) : (
                      <>
                        <PlayCircleIcon className="h-4 w-4" />
                        Förbered och starta
                      </>
                    )}
                  </button>
                </article>
              );
            }
          )}
        </div>
      </section>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="live-round-title"
        >
          <div
            className={`${theme.section} ${theme.textPrimary} flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl shadow-2xl sm:max-h-[90vh] sm:rounded-2xl`}
          >
            <div
              className={`${theme.primary} ${theme.textOnPrimary} flex items-start justify-between gap-4 px-4 py-4 sm:px-6`}
            >
              <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  Digitalt scorekort
                </p>
                <h2
                  id="live-round-title"
                  className="mt-1 truncate text-lg font-semibold sm:text-xl"
                >
                  {selectedRound?.name ||
                    `Runda ${
                      selectedRoundIndex + 1
                    }`}
                </h2>
                <p className="mt-1 text-xs text-white/60">
                  {selectedRound?.modes?.[0] ||
                    "Spelform saknas"}{" "}
                  · {selectedRound?.holes ?? 18} hål
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={isStarting || isSavingDraft || isSending || Boolean(sendingLiveBallId) || Boolean(resendingInviteId) || isPublicLinkBusy}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 disabled:opacity-50"
                aria-label="Stäng"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6">
              {errorMessage && (
                <div
                  className="mb-4 flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                  role="alert"
                >
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="leading-5">
                    {errorMessage}
                  </p>
                </div>
              )}

              {message && (
                <div
                  className="mb-4 flex items-start gap-3 rounded-xl border border-[#bfd5c6] bg-[#f2f8f3] px-3.5 py-3 text-sm text-[#287148]"
                  role="status"
                >
                  <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="leading-5">
                    {message}
                  </p>
                </div>
              )}

              {existingDetails ? (
                <div className="space-y-4">
                  <div
                    className={`${theme.sectionMuted} ${theme.border} flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between`}
                  >
                    <div>
                      <p
                        className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}
                      >
                        Status
                      </p>
                      <p
                        className={`${theme.heading} mt-1 text-base font-semibold`}
                      >
                        {existingDetails.run.status ===
                        "completed"
                          ? "Rundan är avslutad"
                          : "Rundan är live"}
                      </p>
                      <p
                        className={`${theme.textSecondary} mt-1 text-xs`}
                      >
                        {
                          existingDetails.balls
                            .length
                        }{" "}
                        {existingDetails.balls
                          .length === 1
                          ? "boll"
                          : "bollar"}{" "}
                        ·{" "}
                        {
                          existingDetails.invites
                            .length
                        }{" "}
                        länkar
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={
                        isSending ||
                        existingDetails.invites
                          .length === 0
                      }
                      className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                      {isSending ? (
                        <Loader2Icon className="h-4 w-4 animate-spin" />
                      ) : (
                        <SendIcon className="h-4 w-4" />
                      )}
                      {isSending
                        ? "Skickar…"
                        : "Skicka länkar igen"}
                    </button>
                  </div>

                  <section
                    className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}
                  >
                    <div
                      className={`${theme.sectionMuted} ${theme.border} border-b px-4 py-3`}
                    >
                      <div className="flex items-start gap-3">
                        <EyeIcon
                          className={`mt-0.5 h-5 w-5 shrink-0 ${theme.heading}`}
                        />
                        <div>
                          <h3
                            className={`${theme.heading} text-sm font-semibold`}
                          >
                            Publik livevisning
                          </h3>
                          <p
                            className={`${theme.textSecondary} mt-1 text-xs leading-5`}
                          >
                            Dela en separat skrivskyddad länk. Alla med länken kan
                            följa ställningen och scorekorten men aldrig ändra resultat.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      {publicLink?.token ? (
                        <div className="space-y-3">
                          <div
                            className={`${theme.section} ${theme.border} rounded-xl border px-3 py-2.5`}
                          >
                            <p
                              className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.14em]`}
                            >
                              Tittarlänk
                            </p>
                            <p
                              className={`${theme.textPrimary} mt-1 break-all text-xs`}
                            >
                              {getPublicViewerLink(publicLink.token)}
                            </p>
                          </div>

                          <div className="grid gap-2 sm:grid-cols-3">
                            <button
                              type="button"
                              onClick={copyPublicViewerLink}
                              disabled={isPublicLinkBusy}
                              className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition hover:bg-black/[0.025] disabled:opacity-50`}
                            >
                              {copiedPublicLink ? (
                                <CheckCircle2Icon className="h-4 w-4" />
                              ) : (
                                <CopyIcon className="h-4 w-4" />
                              )}
                              {copiedPublicLink ? "Kopierad" : "Kopiera länk"}
                            </button>

                            <a
                              href={getPublicViewerLink(publicLink.token)}
                              target="_blank"
                              rel="noreferrer"
                              className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition`}
                            >
                              <ExternalLinkIcon className="h-4 w-4" />
                              Öppna
                            </a>

                            <button
                              type="button"
                              onClick={handleRevokePublicLink}
                              disabled={isPublicLinkBusy}
                              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3 py-2 text-xs font-semibold text-[#a0443e] transition hover:bg-[#f9e9e6] disabled:opacity-50"
                            >
                              {isPublicLinkBusy ? (
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                              ) : (
                                <EyeOffIcon className="h-4 w-4" />
                              )}
                              Stäng av
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p
                            className={`${theme.textSecondary} max-w-xl text-xs leading-5`}
                          >
                            Ingen publik tittarlänk är aktiv. Spelarnas personliga
                            scorekortslänkar påverkas inte.
                          </p>
                          <button
                            type="button"
                            onClick={handleCreatePublicLink}
                            disabled={isPublicLinkBusy}
                            className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition disabled:opacity-50`}
                          >
                            {isPublicLinkBusy ? (
                              <Loader2Icon className="h-4 w-4 animate-spin" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                            Skapa tittarlänk
                          </button>
                        </div>
                      )}
                    </div>
                  </section>

                  {existingDetails.miniGameHoles?.length > 0 && (
                    <section
                      className={`${theme.tableRow} ${theme.border} rounded-xl border p-4`}
                    >
                      <div className="flex items-start gap-3">
                        <TargetIcon className={`mt-0.5 h-5 w-5 shrink-0 ${theme.accent}`} />
                        <div>
                          <h3 className={`${theme.heading} text-sm font-semibold`}>
                            Aktiva sidotävlingar
                          </h3>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {existingDetails.miniGameHoles.map((config) => (
                              <span
                                key={config.id}
                                className={`${theme.accentSoft} ${theme.accent} rounded-full px-2.5 py-1 text-[10px] font-semibold`}
                              >
                                {config.mini_game_name || getMiniGameTypeLabel(config.game_type)} · hål {config.hole_number}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {existingDetails.balls.map(
                    (ball) => {
                      const ballInvites =
                        existingDetails.invites.filter(
                          (invite) =>
                            invite.live_ball_id ===
                              ball.id &&
                            !invite.revoked_at
                        );

                      return (
                        <section
                          key={ball.id}
                          className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}
                        >
                          <div
                            className={`${theme.sectionMuted} ${theme.border} flex items-center justify-between gap-3 border-b px-4 py-3`}
                          >
                            <div>
                              <p
                                className={`${theme.heading} text-sm font-semibold`}
                              >
                                Boll{" "}
                                {ball.ball_number}
                              </p>
                              <p
                                className={`${theme.textSecondary} mt-0.5 text-[10px]`}
                              >
                                {ball.status ===
                                "submitted"
                                  ? "Inskickad"
                                  : "Pågående"}
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              {ball.status ===
                                "submitted" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleReopenBall(
                                      ball
                                    )
                                  }
                                  className={`${theme.section} ${theme.border} ${theme.heading} rounded-full border px-2.5 py-1 text-[9px] font-semibold transition hover:bg-black/[0.025]`}
                                >
                                  Öppna igen
                                </button>
                              )}

                              <span
                                className={`rounded-full px-2.5 py-1 text-[9px] font-semibold ${
                                  ball.status ===
                                  "submitted"
                                    ? `${theme.accentSoft} ${theme.accent}`
                                    : `${theme.primarySoft} ${theme.positive}`
                                }`}
                              >
                                {ball.status ===
                                "submitted"
                                  ? "Klar"
                                  : "Live"}
                              </span>
                            </div>
                          </div>

                          <div
                            className={`${theme.sectionMuted} ${theme.border} flex items-center justify-between gap-3 border-b px-4 py-2.5`}
                          >
                            <div>
                              <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.15em]`}>
                                Redan skickat till
                              </p>
                              <p className={`${theme.textSecondary} mt-0.5 text-[9px]`}>
                                Dessa adresser har redan en aktiv scorekortslänk.
                              </p>
                            </div>
                            <span className={`${theme.section} ${theme.border} ${theme.textSecondary} shrink-0 rounded-full border px-2.5 py-1 text-[9px] font-semibold`}>
                              {ballInvites.length} {ballInvites.length === 1 ? "mottagare" : "mottagare"}
                            </span>
                          </div>

                          <div className="divide-y divide-[#e4ded1]">
                            {ballInvites.map((invite) => (
                              <div
                                key={invite.id}
                                className="bg-black/[0.012] p-3.5 sm:p-4"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <span
                                    className={`${theme.sectionMuted} ${theme.border} flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border`}
                                  >
                                    <MailIcon className={`h-3.5 w-3.5 ${theme.textSecondary}`} />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className={`${theme.textSecondary} truncate text-xs font-semibold sm:text-sm`}>
                                      {invite.email}
                                    </p>
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                      <span className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} rounded-full border px-2 py-0.5 text-[8px] font-semibold`}>
                                        Redan skickad
                                      </span>
                                      {invite.claimed_at && (
                                        <span className={`${theme.primarySoft} ${theme.positive} rounded-full px-2 py-0.5 text-[8px] font-semibold`}>
                                          Öppnad
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                  <button
                                    type="button"
                                    onClick={() => copyLink(invite.token)}
                                    className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-semibold transition hover:bg-black/[0.025]`}
                                  >
                                    {copiedToken === invite.token ? (
                                      <CheckCircle2Icon className="h-3.5 w-3.5" />
                                    ) : (
                                      <CopyIcon className="h-3.5 w-3.5" />
                                    )}
                                    {copiedToken === invite.token ? "Kopierad" : "Kopiera"}
                                  </button>

                                  <a
                                    href={getLiveLink(invite.token)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-semibold transition hover:bg-black/[0.025]`}
                                  >
                                    <ExternalLinkIcon className="h-3.5 w-3.5" />
                                    Öppna
                                  </a>

                                  <button
                                    type="button"
                                    onClick={() => handleResendInvite(invite)}
                                    disabled={Boolean(resendingInviteId) || Boolean(sendingLiveBallId)}
                                    className={`${theme.primarySoft} ${theme.heading} col-span-2 inline-flex min-h-9 items-center justify-center gap-2 rounded-xl px-3 py-2 text-[10px] font-semibold transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-1`}
                                  >
                                    {resendingInviteId === invite.id ? (
                                      <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <SendIcon className="h-3.5 w-3.5" />
                                    )}
                                    Skicka igen
                                  </button>
                                </div>
                              </div>
                            ))}

                            {ballInvites.length === 0 && (
                              <p className={`${theme.textSecondary} px-4 py-5 text-center text-xs`}>
                                Inga aktiva länkar finns för bollen ännu.
                              </p>
                            )}
                          </div>

                          <div className={`${theme.border} border-t p-4`}>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <h5 className={`${theme.heading} text-xs font-semibold`}>
                                  Skicka till ny adress
                                </h5>
                                <p className={`${theme.textSecondary} mt-1 max-w-lg text-[9px] leading-4`}>
                                  Lägg till en eller flera nya mottagare för boll {ball.ball_number}. Adresser som redan har fått länken visas utgråade ovan och läggs inte till på nytt.
                                </p>
                              </div>
                            </div>

                            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                              <label className="min-w-0 flex-1">
                                <span className={`${theme.textSecondary} mb-1.5 block text-[8px] font-semibold uppercase tracking-[0.15em]`}>
                                  Nya e-postadresser
                                </span>
                                <textarea
                                  rows={2}
                                  value={newEmailsByLiveBall[ball.id] || ""}
                                  onChange={(event) =>
                                    setNewEmailsByLiveBall((current) => ({
                                      ...current,
                                      [ball.id]: event.target.value,
                                    }))
                                  }
                                  placeholder="ny@exempel.se, annan@exempel.se"
                                  className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} w-full resize-y rounded-xl border px-3 py-2.5 text-sm outline-none placeholder:text-[#a0a59f] focus:ring-2 focus:ring-[#183d2d]/20`}
                                />
                              </label>

                              <button
                                type="button"
                                onClick={() => handleSendNewInvites(ball, ballInvites)}
                                disabled={Boolean(sendingLiveBallId) || Boolean(resendingInviteId)}
                                className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50`}
                              >
                                {sendingLiveBallId === ball.id ? (
                                  <Loader2Icon className="h-4 w-4 animate-spin" />
                                ) : (
                                  <SendIcon className="h-4 w-4" />
                                )}
                                Skicka ny länk
                              </button>
                            </div>

                            {ballInvites.length > 0 && (
                              <p className={`${theme.textSecondary} mt-2 text-[8px] leading-4`}>
                                Vill du skicka till en tidigare mottagare igen använder du <span className={`${theme.heading} font-semibold`}>Skicka igen</span> vid den adressen.
                              </p>
                            )}
                          </div>
                        </section>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <section
                    className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection("balls")}
                      className={`${theme.sectionMuted} ${theme.border} flex w-full items-start justify-between gap-3 border-b px-4 py-3.5 text-left sm:px-5`}
                      aria-expanded={expandedSections.balls}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <span
                          className={`${theme.section} ${theme.border} ${theme.textSecondary} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border`}
                        >
                          <UsersIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <h3 className={`${theme.heading} text-sm font-semibold`}>
                            Bollindelning
                          </h3>
                          <p className={`${theme.textSecondary} mt-1 max-w-xl text-xs leading-5`}>
                            Arbeta boll för boll i stället för spelare för spelare.
                            Fördela automatiskt först och finjustera sedan enstaka
                            deltagare eller scramblelag.
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className={`${theme.primarySoft} ${theme.heading} rounded-full px-2.5 py-1 text-[10px] font-semibold`}>
                              {usedBalls.length} {usedBalls.length === 1 ? "boll" : "bollar"}
                            </span>
                            <span className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-2.5 py-1 text-[10px] font-semibold`}>
                              {totalPeople} deltagare
                            </span>
                          </div>
                        </div>
                      </div>
                      <ChevronDownIcon
                        className={`mt-1 h-4 w-4 shrink-0 transition-transform ${theme.textSecondary} ${
                          expandedSections.balls ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {expandedSections.balls && (
                    <div className="p-4">
                      <div
                        className={`${theme.sectionMuted} ${theme.border} mb-4 rounded-xl border p-3`}
                      >
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                          <div className="min-w-0">
                            <p
                              className={`${theme.heading} text-xs font-semibold`}
                            >
                              Automatisk fördelning
                            </p>
                            <p
                              className={`${theme.textSecondary} mt-0.5 text-[10px] leading-4`}
                            >
                              Scramblelag hålls alltid ihop. Befintliga e-postfält
                              töms när du gör en ny automatisk fördelning.
                            </p>
                          </div>

                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={balanceByHandicap}
                              onClick={() => setBalanceByHandicap((current) => !current)}
                              className={`${theme.section} ${theme.border} inline-flex min-h-9 shrink-0 items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-left transition hover:bg-black/[0.025]`}
                              title="När detta är på försöker den automatiska fördelningen jämna ut bollarnas genomsnittliga handicap."
                            >
                              <span
                                className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${
                                  balanceByHandicap
                                    ? "bg-[#183d2d]"
                                    : "bg-[#d5d0c5]"
                                }`}
                              >
                                <span
                                  className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                                    balanceByHandicap
                                      ? "translate-x-3.5"
                                      : "translate-x-0.5"
                                  }`}
                                />
                              </span>
                              <span className={`${theme.heading} whitespace-nowrap text-[10px] font-semibold`}>
                                Balansera HCP
                              </span>
                            </button>

                            <div
                              className={`${theme.section} ${theme.border} inline-flex w-full shrink-0 items-center gap-1 rounded-xl border p-1 sm:w-auto`}
                              aria-label="Välj bollstorlek"
                            >
                              {[4, 3, 2].map((size) => (
                                <button
                                  key={size}
                                  type="button"
                                  onClick={() => autoDistributeBalls(size)}
                                  className={`${theme.heading} flex h-8 min-w-0 flex-1 items-center justify-center rounded-lg px-3 text-[11px] font-semibold leading-none tracking-[-0.01em] transition hover:bg-black/[0.045] sm:min-w-[66px] sm:flex-none`}
                                >
                                  <span className="whitespace-nowrap">{size}-boll</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="relative block w-full sm:max-w-sm">
                          <SearchIcon
                            className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${theme.textSecondary}`}
                          />
                          <input
                            type="search"
                            value={unitSearch}
                            onChange={(event) => setUnitSearch(event.target.value)}
                            placeholder="Sök spelare eller lag…"
                            className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:ring-2 focus:ring-[#183d2d]/20`}
                          />
                        </label>

                        <p
                          className={`${theme.textSecondary} text-[10px] leading-4`}
                        >
                          Högst 4 faktiska personer per boll.
                        </p>
                      </div>

                      {filteredBalls.length === 0 ? (
                        <div
                          className={`${theme.sectionMuted} ${theme.border} rounded-xl border px-4 py-8 text-center`}
                        >
                          <p className={`${theme.textPrimary} text-sm font-semibold`}>
                            Ingen deltagare hittades
                          </p>
                          <p className={`${theme.textSecondary} mt-1 text-xs`}>
                            Prova ett annat namn i sökfältet.
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-3 lg:grid-cols-2">
                          {filteredBalls.map((ball) => {
                            const allBallIndex = usedBalls.findIndex(
                              (item) => item.ballNumber === ball.ballNumber
                            );
                            const previousBall = usedBalls[allBallIndex - 1];
                            const nextBall = usedBalls[allBallIndex + 1];
                            const averageHandicap = getBallAverageHandicap(ball);

                            return (
                              <article
                                key={ball.ballNumber}
                                className={`${theme.section} ${theme.border} overflow-hidden rounded-xl border`}
                              >
                                <div
                                  className={`${theme.sectionMuted} ${theme.border} flex items-center justify-between gap-3 border-b px-3.5 py-3`}
                                >
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p
                                        className={`${theme.heading} text-sm font-semibold`}
                                      >
                                        Boll {ball.ballNumber}
                                      </p>

                                      {averageHandicap !== null && (
                                        <span
                                          className={`${theme.section} ${theme.border} ${theme.textSecondary} inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[8px] font-semibold tabular-nums shadow-[0_1px_0_rgba(24,61,45,0.03)]`}
                                          title="Genomsnittligt handicap för spelarna i bollen"
                                        >
                                          <FlagIcon className="h-2.5 w-2.5 text-[#9a7b3f]" />
                                          Snitt-HCP {formatHandicap(averageHandicap)}
                                        </span>
                                      )}
                                    </div>
                                    <p
                                      className={`${theme.textSecondary} mt-0.5 text-[10px]`}
                                    >
                                      {ball.units.length}{" "}
                                      {ball.units.length === 1 ? "scorenhet" : "scorenheter"}
                                    </p>
                                  </div>

                                  <span
                                    className={`rounded-full px-2.5 py-1 text-[9px] font-semibold ${
                                      ball.people > 4
                                        ? "bg-[#f3e8e5] text-[#a0443e]"
                                        : ball.people === 4
                                          ? `${theme.primarySoft} ${theme.heading}`
                                          : `${theme.section} ${theme.border} ${theme.textSecondary} border`
                                    }`}
                                  >
                                    {ball.people}/4 personer
                                  </span>
                                </div>

                                <div className="divide-y divide-[#e4ded1]">
                                  {ball.units.map((unit) => {
                                    const memberCount = Number(unit.memberCount) || 1;
                                    const canMoveLeft = Boolean(
                                      previousBall &&
                                        previousBall.people + memberCount <= 4
                                    );
                                    const canMoveRight =
                                      !nextBall || nextBall.people + memberCount <= 4;
                                    const availableBallNumbers = [
                                      ...usedBalls
                                        .filter(
                                          (target) =>
                                            target.ballNumber === ball.ballNumber ||
                                            target.people + memberCount <= 4
                                        )
                                        .map((target) => target.ballNumber),
                                      nextBallNumber,
                                    ];

                                    return (
                                      <div
                                        key={unit.clientKey}
                                        className="flex items-center gap-2.5 px-3 py-3"
                                      >
                                        <div className="min-w-0 flex-1">
                                          <p
                                            className={`${theme.textPrimary} truncate text-xs font-semibold sm:text-sm`}
                                            title={unit.label}
                                          >
                                            {unit.label}
                                          </p>
                                          <p
                                            className={`${theme.textSecondary} mt-0.5 truncate text-[9px]`}
                                          >
                                            {unit.unitType === "team"
                                              ? `${memberCount} personer · Lag-HCP ${formatHandicap(
                                                  unit.fullHandicap
                                                )}`
                                              : unit.roundDeduction > 0
                                                ? `HCP ${formatHandicap(
                                                    unit.fullHandicap
                                                  )} · Avdrag ${unit.roundDeduction}`
                                                : `HCP ${formatHandicap(
                                                    unit.fullHandicap
                                                  )}`}
                                            {tournament.rounds.length > 1
                                              ? ` · Tidigare ${unit.previousTotal}`
                                              : ""}
                                          </p>
                                        </div>

                                        <div className="flex shrink-0 items-center gap-1">
                                          <button
                                            type="button"
                                            onClick={() => moveUnitByOffset(unit, -1)}
                                            disabled={!canMoveLeft}
                                            className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} flex h-8 w-8 items-center justify-center rounded-lg border transition hover:bg-black/[0.035] disabled:cursor-not-allowed disabled:opacity-25`}
                                            title="Flytta till föregående boll"
                                            aria-label={`Flytta ${unit.label} till föregående boll`}
                                          >
                                            <ChevronLeftIcon className="h-4 w-4" />
                                          </button>

                                          <select
                                            value={unit.ballNumber}
                                            onChange={(event) =>
                                              updateUnitBall(
                                                unit.clientKey,
                                                Number(event.target.value)
                                              )
                                            }
                                            className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} h-8 min-w-[66px] rounded-lg border px-2 text-[10px] font-semibold outline-none focus:ring-2 focus:ring-[#183d2d]/20`}
                                            aria-label={`Välj boll för ${unit.label}`}
                                          >
                                            {[...new Set(availableBallNumbers)].map(
                                              (ballNumber) => (
                                                <option
                                                  key={ballNumber}
                                                  value={ballNumber}
                                                >
                                                  Boll {ballNumber}
                                                  {ballNumber === nextBallNumber
                                                    ? " (ny)"
                                                    : ""}
                                                </option>
                                              )
                                            )}
                                          </select>

                                          <button
                                            type="button"
                                            onClick={() => moveUnitByOffset(unit, 1)}
                                            disabled={!canMoveRight}
                                            className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} flex h-8 w-8 items-center justify-center rounded-lg border transition hover:bg-black/[0.035] disabled:cursor-not-allowed disabled:opacity-25`}
                                            title={nextBall ? "Flytta till nästa boll" : "Skapa ny boll"}
                                            aria-label={
                                              nextBall
                                                ? `Flytta ${unit.label} till nästa boll`
                                                : `Flytta ${unit.label} till en ny boll`
                                            }
                                          >
                                            <ChevronRightIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    )}
                  </section>


                  <section
                    className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}
                  >
                    {hasSelectedCourse ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            if (expandedSections.holeInfo) {
                              setCourseInfoEditing(false);
                            }
                            toggleSection("holeInfo");
                          }}
                          className={`${theme.sectionMuted} ${theme.border} flex w-full items-start justify-between gap-3 border-b px-4 py-3.5 text-left sm:px-5`}
                          aria-expanded={expandedSections.holeInfo}
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span
                              className={`${theme.section} ${theme.border} ${theme.textSecondary} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border`}
                            >
                              <FlagIcon className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className={`${theme.heading} text-sm font-semibold`}>
                                  Bana & hålinformation
                                </h3>
                                {hasCourseOverrides && (
                                  <span className="rounded-full bg-[#fbf0df] px-2 py-0.5 text-[8px] font-semibold text-[#8a6623]">
                                    Tillfälligt ändrad
                                  </span>
                                )}
                              </div>
                              <p className={`${theme.textSecondary} mt-1 truncate text-[10px] leading-4 sm:text-xs`}>
                                {courseSnapshot?.courseName || selectedRound?.name || "Vald golfbana"}
                                {courseSnapshot
                                  ? ` · par ${courseSnapshot.pars.reduce((sum, value) => sum + Number(value || 0), 0)} · par och index hämtas automatiskt`
                                  : " · par och index hämtas automatiskt"}
                              </p>
                            </div>
                          </div>
                          <ChevronDownIcon
                            className={`mt-1 h-4 w-4 shrink-0 transition-transform ${theme.textSecondary} ${
                              expandedSections.holeInfo ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {expandedSections.holeInfo && (
                          <div className="p-4 sm:p-5">
                            {courseLoading ? (
                              <div className={`${theme.textSecondary} flex min-h-24 items-center justify-center gap-2 text-xs`}>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Hämtar baninformation…
                              </div>
                            ) : courseSnapshot ? (
                              <div className="space-y-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                  <div className="min-w-0">
                                    <p className={`${theme.heading} truncate text-sm font-semibold`}>
                                      {courseSnapshot.courseName}
                                    </p>
                                    <p className={`${theme.textSecondary} mt-0.5 text-[9px] leading-4`}>
                                      {courseSnapshot.clubName || "Golfbana"}
                                      {courseSnapshot.teeName ? ` · ${courseSnapshot.teeName}` : ""}
                                      {courseSnapshot.category
                                        ? ` · ${courseSnapshot.category === "men" ? "Herr" : "Dam"}`
                                        : ""}
                                    </p>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => setCourseInfoEditing((current) => !current)}
                                    aria-pressed={courseInfoEditing}
                                    className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-9 shrink-0 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-semibold transition hover:bg-black/[0.025]`}
                                  >
                                    {courseInfoEditing ? (
                                      <CheckCircle2Icon className="h-3.5 w-3.5" />
                                    ) : (
                                      <PencilIcon className="h-3.5 w-3.5" />
                                    )}
                                    {courseInfoEditing ? "Klar" : "Tillfällig ändring"}
                                  </button>
                                </div>

                                <div className={`${theme.primarySoft} ${theme.border} flex items-start gap-2.5 rounded-xl border px-3 py-2.5`}>
                                  <InfoIcon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${theme.textSecondary}`} />
                                  <p className={`${theme.textSecondary} text-[9px] leading-4 sm:text-[10px]`}>
                                    Uppgifterna nedan används automatiskt från den valda banan. Ändra endast om något på banan tillfälligt avviker inför just den här starten, till exempel provisorisk green eller ändrad hålordning.
                                  </p>
                                </div>

                                {courseInfoEditing && (
                                  <div className="flex items-center justify-between gap-3 rounded-xl border border-[#ead9b7] bg-[#fffaf0] px-3 py-2.5">
                                    <p className="text-[9px] leading-4 text-[#8a6623]">
                                      Ändringarna gäller bara denna live-runda och påverkar inte banregistret.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setHolePars([...courseSnapshot.pars]);
                                        setHoleIndexes([...courseSnapshot.indexes]);
                                      }}
                                      disabled={!hasCourseOverrides}
                                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-[9px] font-semibold text-[#8a6623] transition hover:bg-[#f7edda] disabled:cursor-default disabled:opacity-40"
                                    >
                                      <RefreshCwIcon className="h-3 w-3" />
                                      Återställ
                                    </button>
                                  </div>
                                )}

                                <div className={`transition-opacity ${courseInfoEditing ? "opacity-100" : "opacity-[0.82]"}`}>
                                  {renderHoleInformationGrid({
                                    editable: courseInfoEditing,
                                    showCourseBaseline: true,
                                  })}
                                </div>

                                <div className={`${theme.sectionMuted} ${theme.border} flex items-center justify-between rounded-xl border px-3 py-2`}>
                                  <span className={`${theme.textSecondary} text-[9px] font-medium`}>
                                    Totalpar
                                  </span>
                                  <span className={`${theme.heading} text-sm font-semibold tabular-nums`}>
                                    {holePars.reduce((sum, value) => sum + (Number(value) || 0), 0)}
                                  </span>
                                </div>

                                {courseSnapshot.usesFrontNine && Number(selectedRound?.holes || 18) === 9 && (
                                  <p className={`${theme.textSecondary} text-[8px] leading-4`}>
                                    Rundan är 9 hål på en 18-hålsbana. Hål 1–9 används som utgångspunkt.
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="rounded-xl border border-[#e4cfaa] bg-[#fffaf0] px-3.5 py-3 text-[10px] leading-4 text-[#8a6623]">
                                Den valda banan är kopplad till rundan, men komplett par- och indexinformation kunde inte hämtas. Kontrollera bandatan innan start.
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          className={`${theme.sectionMuted} ${theme.border} flex items-center gap-2 border-b px-4 py-3.5 sm:px-5`}
                        >
                          <button
                            type="button"
                            onClick={() => parEnabled && toggleSection("holeInfo")}
                            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                            aria-expanded={parEnabled && expandedSections.holeInfo}
                          >
                            <div className="flex min-w-0 items-start gap-3">
                              <span
                                className={`${theme.section} ${theme.border} ${theme.textSecondary} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border`}
                              >
                                <FlagIcon className="h-4 w-4" />
                              </span>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className={`${theme.heading} text-sm font-semibold`}>
                                    Par & index
                                  </h3>
                                  <span className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.12em]`}>
                                    Valfritt
                                  </span>
                                </div>
                                <p className={`${theme.textSecondary} mt-1 max-w-xl text-[10px] leading-4 sm:text-xs`}>
                                  Ingen golfbana är kopplad. Lägg till hålinformation om du vill använda par och index i live-scorekortet.
                                </p>
                              </div>
                            </div>

                            {parEnabled && (
                              <ChevronDownIcon
                                className={`h-4 w-4 shrink-0 transition-transform ${theme.textSecondary} ${
                                  expandedSections.holeInfo ? "rotate-180" : ""
                                }`}
                              />
                            )}
                          </button>

                          <button
                            type="button"
                            role="switch"
                            aria-checked={parEnabled}
                            onClick={() => {
                              const nextValue = !parEnabled;
                              setParEnabled(nextValue);
                              setExpandedSections((current) => ({
                                ...current,
                                holeInfo: nextValue,
                              }));
                            }}
                            className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                              parEnabled
                                ? theme.primary
                                : `${theme.section} ${theme.borderStrong} border`
                            }`}
                          >
                            <span
                              className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition ${
                                parEnabled ? "left-6" : "left-1"
                              }`}
                            />
                            <span className="sr-only">
                              {parEnabled ? "Ta bort par och index" : "Lägg till par och index"}
                            </span>
                          </button>
                        </div>

                        {parEnabled && expandedSections.holeInfo && (
                          <div className="p-4 sm:p-5">
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                              <p className={`${theme.textSecondary} max-w-xl text-[9px] leading-4`}>
                                Ange par 2–7 och ett unikt hålindex 1–18 för varje hål.
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  const holeCount = Number(selectedRound?.holes || 18);
                                  setHolePars(Array.from({ length: holeCount }, () => 4));
                                  setHoleIndexes(Array.from({ length: holeCount }, (_, index) => index + 1));
                                }}
                                className={`${theme.section} ${theme.border} ${theme.textSecondary} inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[9px] font-semibold transition hover:bg-black/[0.025]`}
                              >
                                <RefreshCwIcon className="h-3 w-3" />
                                Återställ
                              </button>
                            </div>

                            {renderHoleInformationGrid({ editable: true })}

                            <div className={`${theme.sectionMuted} ${theme.border} mt-4 flex items-center justify-between rounded-xl border px-3 py-2`}>
                              <span className={`${theme.textSecondary} text-[9px] font-medium`}>
                                Totalpar
                              </span>
                              <span className={`${theme.heading} text-sm font-semibold tabular-nums`}>
                                {holePars.reduce((sum, value) => sum + (Number(value) || 0), 0)}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </section>



                  {miniGameConfigs.length > 0 && (
                    <section className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}>
                      <button
                        type="button"
                        onClick={() => toggleSection("miniGames")}
                        className={`${theme.sectionMuted} ${theme.border} flex w-full items-start justify-between gap-3 border-b px-4 py-3.5 text-left sm:px-5`}
                        aria-expanded={expandedSections.miniGames}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <span
                            className={`${theme.section} ${theme.border} ${theme.accent} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border`}
                          >
                            <TargetIcon className="h-4 w-4" />
                          </span>
                          <div>
                            <h3 className={`${theme.heading} text-sm font-semibold`}>Sidotävlingar</h3>
                            <p className={`${theme.textSecondary} mt-1 text-xs leading-5`}>
                              Välj vilka hål som ska användas för respektive sidotävling.
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className={`${theme.accentSoft} ${theme.accent} rounded-full px-2.5 py-1 text-[9px] font-semibold`}>
                            {miniGameConfigs.reduce((sum, config) => sum + config.selectedHoles.length, 0)} val
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 transition-transform ${theme.textSecondary} ${expandedSections.miniGames ? "rotate-180" : ""}`} />
                        </div>
                      </button>

                      {expandedSections.miniGames && (
                        <div className="space-y-5 p-4">
                          {miniGameConfigs.map((config) => {
                            const isClosestToPin = config.gameType === "closest_to_pin";
                            return (
                              <div key={config.miniGameId} className={`${theme.section} ${theme.border} rounded-xl border p-3.5`}>
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div>
                                    <p className={`${theme.textPrimary} text-sm font-semibold`}>{config.name}</p>
                                    <p className={`${theme.textSecondary} mt-0.5 inline-flex items-center gap-1 text-[10px]`}>
                                      {isClosestToPin ? <TargetIcon className="h-3 w-3" /> : <RulerIcon className="h-3 w-3" />}
                                      {getMiniGameTypeLabel(config.gameType)}
                                      {isClosestToPin && parThreeHoles.length > 0 ? ` · ${parThreeHoles.length} par 3-hål` : ""}
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    {isClosestToPin && parThreeHoles.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => selectAllParThrees(config.miniGameId)}
                                        className={`${theme.primarySoft} ${theme.heading} rounded-lg px-2.5 py-1.5 text-[9px] font-semibold transition hover:opacity-80`}
                                      >
                                        Välj alla par 3
                                      </button>
                                    )}
                                    <span className={`${theme.accentSoft} ${theme.accent} rounded-full px-2.5 py-1 text-[9px] font-semibold`}>
                                      {config.selectedHoles.length || 0} valda
                                    </span>
                                  </div>
                                </div>

                                <div className="mt-3 space-y-3">
                                  {holeSegments.map((segment) => (
                                    <div key={`${config.miniGameId}-${segment.key}`}>
                                      <p className={`${theme.textSecondary} mb-1.5 text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                        {segment.label}
                                      </p>
                                      <div className="grid grid-cols-3 gap-x-1.5 gap-y-3 sm:grid-cols-9 sm:gap-x-2">
                                        {segment.holes.map((holeNumber) => {
                                          const selected = config.selectedHoles.includes(holeNumber);
                                          const isParThree = isClosestToPin && holeInfoEnabled && Number(holePars[holeNumber - 1]) === 3;

                                          return (
                                            <button
                                              key={holeNumber}
                                              type="button"
                                              onClick={() => toggleMiniGameHole(config.miniGameId, holeNumber)}
                                              className={`relative flex h-11 min-w-0 items-center justify-center rounded-xl border px-1 pt-0.5 text-[11px] font-semibold leading-none tabular-nums transition-all duration-150 sm:h-12 ${
                                                selected
                                                  ? `${theme.accentBackground} border-transparent text-white shadow-sm ring-1 ring-black/[0.04]`
                                                  : isParThree
                                                    ? "border-[#d5c49b] bg-[#fbf8f1] text-[#354239] hover:border-[#c6b27f] hover:bg-[#f9f4e9]"
                                                    : `${theme.section} ${theme.border} ${theme.textSecondary} hover:border-[#c5cbc6] hover:bg-black/[0.018]`
                                              }`}
                                              aria-pressed={selected}
                                              title={isParThree ? `Hål ${holeNumber} · par 3 · rekommenderat för closest to pin` : `Hål ${holeNumber}`}
                                            >
                                              {isParThree && (
                                                <span
                                                  className={`pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border px-1.5 py-[2px] text-[6px] font-bold uppercase tracking-[0.1em] ${
                                                    selected
                                                      ? "border-white/25 bg-white/15 text-white"
                                                      : "border-[#d5c49b] bg-[#fffdf8] text-[#7c6632]"
                                                  }`}
                                                >
                                                  Par 3
                                                </span>
                                              )}
                                              <span className={selected ? "text-[12px]" : ""}>{holeNumber}</span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {isClosestToPin && holeInfoEnabled && parThreeHoles.length === 0 && (
                                  <p className="mt-2 text-[10px] leading-4 text-[#8a6623]">
                                    Inga hål är registrerade som par 3 i rundans baninformation.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                  <section
                    className={`${theme.tableRow} ${theme.border} overflow-hidden rounded-xl border`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection("invites")}
                      className={`${theme.sectionMuted} ${theme.border} flex w-full items-start justify-between gap-3 border-b px-4 py-3.5 text-left sm:px-5`}
                      aria-expanded={expandedSections.invites}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <span
                          className={`${theme.section} ${theme.border} ${theme.textSecondary} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border`}
                        >
                          <MailIcon className="h-4 w-4" />
                        </span>
                        <div>
                          <h3 className={`${theme.heading} text-sm font-semibold`}>Skicka länkar</h3>
                          <p className={`${theme.textSecondary} mt-1 text-xs leading-5`}>
                            Ange vilka e-postadresser som ska få scorekortslänken för respektive boll.
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {ballsMissingEmails.length > 0 ? (
                          <span className="rounded-full bg-[#f3e8e5] px-2.5 py-1 text-[9px] font-semibold text-[#a0443e]">
                            {ballsMissingEmails.length} {ballsMissingEmails.length === 1 ? "boll saknar mejl" : "bollar saknar mejl"}
                          </span>
                        ) : ballsWithInvalidEmails.length > 0 ? (
                          <span className="rounded-full bg-[#fbf0df] px-2.5 py-1 text-[9px] font-semibold text-[#8a6623]">
                            Kontrollera mejl
                          </span>
                        ) : (
                          <span className={`${theme.primarySoft} ${theme.positive} rounded-full px-2.5 py-1 text-[9px] font-semibold`}>
                            Alla bollar klara
                          </span>
                        )}
                        <ChevronDownIcon className={`h-4 w-4 transition-transform ${theme.textSecondary} ${expandedSections.invites ? "rotate-180" : ""}`} />
                      </div>
                    </button>

                    {expandedSections.invites && (
                    <div className="space-y-3 p-4">
                      {usedBalls.map((ball) => (
                        <section
                          key={ball.ballNumber}
                          className={`${theme.section} ${theme.border} overflow-hidden rounded-xl border`}
                        >
                          <div
                            className={`${theme.sectionMuted} ${theme.border} flex items-center justify-between gap-3 border-b px-4 py-3`}
                          >
                            <div>
                              <h4
                                className={`${theme.heading} text-sm font-semibold`}
                              >
                                Boll {ball.ballNumber}
                              </h4>
                              <p
                                className={`${theme.textSecondary} mt-0.5 text-[10px]`}
                              >
                                {ball.units
                                  .map((unit) => unit.label)
                                  .join(", ")}
                              </p>
                            </div>

                            <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center">
                              <span
                                className={`rounded-full px-2.5 py-1 text-[9px] font-semibold ${
                                  ball.people > 4
                                    ? "bg-[#f3e8e5] text-[#a0443e]"
                                    : `${theme.primarySoft} ${theme.heading}`
                                }`}
                              >
                                {ball.people}/4 personer
                              </span>
                              {splitEmails(emailsByBall[ball.ballNumber]).length === 0 ? (
                                <span className="rounded-full bg-[#f3e8e5] px-2.5 py-1 text-[9px] font-semibold text-[#a0443e]">
                                  Mejladress saknas
                                </span>
                              ) : (
                                <span className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-2.5 py-1 text-[9px] font-semibold`}>
                                  {splitEmails(emailsByBall[ball.ballNumber]).length} mejl
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-4">
                            <label
                              htmlFor={`emails-ball-${ball.ballNumber}`}
                              className={`${theme.textSecondary} mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.16em]`}
                            >
                              E-postadresser
                            </label>

                            <textarea
                              id={`emails-ball-${ball.ballNumber}`}
                              rows={2}
                              value={emailsByBall[ball.ballNumber] || ""}
                              onChange={(event) =>
                                setEmailsByBall((current) => ({
                                  ...current,
                                  [ball.ballNumber]: event.target.value,
                                }))
                              }
                              placeholder="namn@exempel.se, annan@exempel.se"
                              className={`${theme.section} ${theme.borderStrong} ${theme.textPrimary} w-full resize-y rounded-xl border px-3 py-2.5 text-sm outline-none placeholder:text-[#a0a59f] focus:ring-2 focus:ring-[#183d2d]/20`}
                            />

                            <p
                              className={`${theme.textSecondary} mt-1.5 text-[10px] leading-4`}
                            >
                              Minst en och högst {ball.people}. Separera flera
                              adresser med komma eller ny rad.
                            </p>
                          </div>
                        </section>
                      ))}
                    </div>
                    )}
                  </section>
                </div>
              )}
            </div>

            <div
              className={`${theme.sectionMuted} ${theme.border} flex flex-col-reverse gap-2 border-t px-3 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-6`}
            >
              <button
                type="button"
                onClick={closeModal}
                disabled={isStarting || isSavingDraft || isSending || Boolean(sendingLiveBallId) || Boolean(resendingInviteId) || isPublicLinkBusy}
                className={`${theme.section} ${theme.border} ${theme.textSecondary} min-h-11 rounded-xl border px-4 py-2 text-sm font-semibold transition hover:bg-black/[0.025] disabled:opacity-50`}
              >
                Stäng
              </button>

              {!existingDetails && (
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={isSavingDraft || isStarting}
                  className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition hover:bg-black/[0.025] disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {isSavingDraft ? (
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  ) : (
                    <SaveIcon className="h-4 w-4" />
                  )}
                  {isSavingDraft
                    ? "Sparar…"
                    : roundDrafts[selectedRound?.id]
                      ? "Spara ändringar"
                      : "Spara förberedelser"}
                </button>
              )}

              {!existingDetails && (
                <button
                  type="button"
                  onClick={handleStartRound}
                  disabled={isStarting || isSavingDraft || !allBallsReadyForStart}
                  title={
                    !allBallsReadyForStart
                      ? "Lägg till minst en giltig e-postadress för varje boll innan rundan kan startas."
                      : undefined
                  }
                  className={`${theme.primary} ${theme.primaryHover} ${theme.textOnPrimary} inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {isStarting ? (
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  ) : (
                    <PlayCircleIcon className="h-4 w-4" />
                  )}
                  {isStarting
                    ? "Startar rundan…"
                    : "Starta och skicka spelarlänkar"}
                </button>
              )}

              {existingDetails && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await fetchExistingDetails(
                        existingDetails.run
                      );
                      setMessage(
                        "Statusen har uppdaterats."
                      );
                    } catch (error) {
                      console.error(error);
                      setErrorMessage(
                        "Statusen kunde inte uppdateras."
                      );
                    }
                  }}
                  className={`${theme.section} ${theme.border} ${theme.heading} inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition hover:bg-black/[0.025]`}
                >
                  <RefreshCwIcon className="h-4 w-4" />
                  Uppdatera status
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}